import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import StudentDashboard from './pages/StudentDashboard';
import CompanyDashboard from './pages/CompanyDashboard';
import AdminDashboard from './pages/AdminDashboard';
import JobListings from './pages/JobListings';
import JobDetails from './pages/JobDetails';
import Profile from './pages/Profile';
import Applications from './pages/Applications';
import LoadingSpinner from './components/LoadingSpinner';

const PrivateRoute = ({ children, allowedUserTypes }) => {
  const { isAuthenticated, userType, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (allowedUserTypes && !allowedUserTypes.includes(userType)) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  const { isAuthenticated, userType } = useAuth();

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 transition-colors duration-200">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />} />
          <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/dashboard" />} />
          
          {/* Student Routes */}
          <Route 
            path="/student/dashboard" 
            element={
              <PrivateRoute allowedUserTypes={['student']}>
                <StudentDashboard />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/student/jobs" 
            element={
              <PrivateRoute allowedUserTypes={['student']}>
                <JobListings />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/student/jobs/:id" 
            element={
              <PrivateRoute allowedUserTypes={['student']}>
                <JobDetails />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/student/applications" 
            element={
              <PrivateRoute allowedUserTypes={['student']}>
                <Applications />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/student/profile" 
            element={
              <PrivateRoute allowedUserTypes={['student']}>
                <Profile />
              </PrivateRoute>
            } 
          />
          
          {/* Company Routes */}
          <Route 
            path="/company/dashboard" 
            element={
              <PrivateRoute allowedUserTypes={['company']}>
                <CompanyDashboard />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/company/profile" 
            element={
              <PrivateRoute allowedUserTypes={['company']}>
                <Profile />
              </PrivateRoute>
            } 
          />
          
          {/* Admin Routes */}
          <Route 
            path="/admin/dashboard" 
            element={
              <PrivateRoute allowedUserTypes={['admin']}>
                <AdminDashboard />
              </PrivateRoute>
            } 
          />
          
          {/* Redirect authenticated users to their dashboard */}
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                userType === 'student' ? <Navigate to="/student/dashboard" /> :
                userType === 'company' ? <Navigate to="/company/dashboard" /> :
                userType === 'admin' ? <Navigate to="/admin/dashboard" /> :
                <Navigate to="/" />
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
